import React, { memo } from "react";

import { BrandJustLogo } from "@hl/base-components/lib/assets/icons.generated";
import { envConfig } from "@hl/shared-features/lib/config";
import UserSkeleton from "@hl/shared-features/lib/features/auth/UserSkeleton";
import {
  Anchor,
  Container,
  Flex,
  Group,
  MediaQuery,
  Space,
  Stack,
  Divider,
  createStyles,
  useMantineTheme,
  Skeleton,
  AppShell,
  Card,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import MainCardSkeleton from "~features/MintPage/MintVector/MintCardSkeleton";
import { AppHeader } from "~features/layout/Header";

const useStyles = createStyles((theme) => {
  const mdUp = `@media (min-width: ${theme.breakpoints.md}px)`;

  return {
    layout: {
      flexDirection: "column",
      [mdUp]: {
        flexDirection: "row-reverse",
      },
    },
    leftColumn: {
      width: "100%",
      [mdUp]: {
        paddingRight: 64,
        flexGrow: 1,
      },
    },
  };
});

const MintPageSkeleton = () => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);

  const { classes } = useStyles();

  const TEXT_HEIGHT = 20;

  const skeletonItems = (count: number, skeleton: React.ReactNode) => {
    const rows = [];
    for (let i = 0; i < count; i++) {
      rows.push(<React.Fragment key={i}>{skeleton}</React.Fragment>);
    }
    return rows;
  };

  return (
    <AppShell header={<AppHeader />}>
      <Stack
        align="center"
        spacing={isMobile ? 40 : 64}
        style={{ overflowX: "hidden" }}
        pb={64}
        px={16}
        mx={-16}
        pt={isMobile ? 0 : 36}
      >
        <Stack align="center" spacing={16} w="100%">
          <UserSkeleton />
          <Stack spacing={16} align="center" w="100%" mb={0}>
            <Skeleton
              height={isMobile ? 30 : 60}
              width={isMobile ? 200 : 450}
            />
            <Skeleton height={TEXT_HEIGHT} width={100} />
            <Card shadow="sm" p={0} withBorder={false}>
              <Skeleton
                height={isMobile ? 420 : 540}
                width={isMobile ? 320 : 540}
                radius={0}
              />
            </Card>
          </Stack>
        </Stack>
      </Stack>
      <Container size="md" mb={140}>
        <Flex className={classes.layout}>
          <Box>
            <MainCardSkeleton />
          </Box>
          <Stack className={classes.leftColumn}>
            <MediaQuery largerThan="md" styles={{ display: "none" }}>
              <Space h={40} />
            </MediaQuery>
            <Stack>
              {skeletonItems(5, <Skeleton height={TEXT_HEIGHT} />)}
              <Skeleton height={TEXT_HEIGHT} width="60%" />
            </Stack>

            <Group mt={40}>
              {skeletonItems(3, <Skeleton height={TEXT_HEIGHT} width={100} />)}
            </Group>
            <Divider />
            <Stack>
              {skeletonItems(
                5,
                <>
                  <Stack spacing={4}>
                    <Skeleton height={15} width={50} />
                    <Skeleton height={TEXT_HEIGHT} width={100} />
                  </Stack>
                  <Divider />
                </>
              )}
            </Stack>
          </Stack>
        </Flex>
      </Container>
      <Flex justify="center">
        <Anchor href={envConfig.home.baseUrl} target="_blank" rel="noreferrer">
          <BrandJustLogo
            height={14}
            width={20}
            color={theme.colors.primaryText[0]}
          />
        </Anchor>
      </Flex>
      <Space h={20} />
    </AppShell>
  );
};

export default memo(MintPageSkeleton);
